import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SEO } from '@/components/SEO';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Target, Users, Rocket, Heart } from 'lucide-react';
import logo from '@/assets/logo.png';
import { generateBreadcrumbSchema, generateLocalBusinessSchema } from '@/lib/structuredData';

export default function About() {
  const navigate = useNavigate();

  const values = [
    {
      icon: Target,
      title: "Accuracy First",
      description: "We're obsessed with precision. Every algorithm is tuned to deliver 99%+ accuracy in financial reconciliation."
    },
    {
      icon: Users,
      title: "Customer Success",
      description: "Your success is our success. We provide world-class support and continuously improve based on your feedback."
    },
    {
      icon: Rocket,
      title: "Innovation",
      description: "We leverage cutting-edge AI technology to solve real-world financial challenges faster and better."
    },
    {
      icon: Heart,
      title: "Trust & Security",
      description: "We treat your financial data with the highest security standards and complete transparency."
    }
  ];

  const team = [
    {
      name: "The Founders",
      role: "Building the Future of Finance",
      description: "A team of fintech veterans and AI engineers passionate about eliminating manual financial work."
    }
  ];

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://getsentinel.pro/' },
    { name: 'About', url: 'https://getsentinel.pro/about' }
  ]);

  const localBusinessSchema = generateLocalBusinessSchema();

  return (
    <>
      <SEO
        title="About Us - Meet the Sentinel Team | Sentinel"
        description="Learn about Sentinel's mission to transform financial reconciliation with AI. Meet our team and discover our values."
      />
      
      {/* Breadcrumb Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      
      {/* LocalBusiness Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }} />
      
      <div className="min-h-screen bg-background">
        {/* Navigation */}
        <nav className="border-b border-border/50 bg-background/95 backdrop-blur-xl sticky top-0 z-50">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
                <img src={logo} alt="SENTINEL Logo" className="w-10 h-10 brightness-0 dark:brightness-100 dark:invert" />
                <span className="text-2xl font-bold">SENTINEL</span>
              </div>
              <div className="flex items-center gap-4">
                <ThemeToggle />
                <Button onClick={() => navigate('/auth')}>Sign In</Button>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-hero" />
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Transforming Finance with
                <span className="bg-gradient-primary bg-clip-text text-transparent"> Intelligent Automation</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                We're on a mission to eliminate tedious financial reconciliation work and empower finance teams to focus on strategic decisions.
              </p>
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="py-20">
          <div className="container mx-auto px-6 max-w-4xl">
            <Card className="p-12 border-border/50">
              <h2 className="text-4xl font-bold mb-6">Our Story</h2>
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  Sentinel was born from frustration. After watching finance teams spend countless hours manually matching transactions and hunting down invoice duplicates, we knew there had to be a better way.
                </p>
                <p>
                  Traditional reconciliation tools are slow, error-prone, and expensive. They weren't built for the AI era. So we built Sentinel—a modern platform that combines advanced AI with financial domain expertise to automate the grunt work.
                </p>
                <p>
                  Today, Sentinel helps businesses across India save thousands of hours and millions of rupees by catching duplicate payments, automating reconciliation, and providing real-time financial intelligence.
                </p>
                <p>
                  We're just getting started. Our vision is to make financial operations so intelligent and automated that finance teams can focus entirely on growth and strategy.
                </p>
              </div>
            </Card>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 bg-surface/30">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">Our Values</h2>
              <p className="text-xl text-muted-foreground">
                The principles that guide everything we do
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
              {values.map((value, index) => (
                <Card key={index} className="p-6 text-center border-border/50 hover:shadow-card-hover transition-all">
                  <div className="w-14 h-14 rounded-xl bg-gradient-primary flex items-center justify-center mx-auto mb-4 shadow-glow">
                    <value.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                  <p className="text-muted-foreground">{value.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">Meet the Team</h2>
              <p className="text-xl text-muted-foreground">
                The people building the future of financial operations
              </p>
            </div>

            <div className="max-w-2xl mx-auto">
              {team.map((member, index) => (
                <Card key={index} className="p-8 border-border/50 text-center">
                  <h3 className="text-2xl font-bold mb-2">{member.name}</h3>
                  <p className="text-primary font-medium mb-4">{member.role}</p>
                  <p className="text-muted-foreground">{member.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-primary relative overflow-hidden">
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Join Us on This Journey
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Whether you're looking to automate your finance operations or join our team, we'd love to hear from you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" onClick={() => navigate('/auth')}>
                  Get Started Free
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate('/support')} className="text-white border-white hover:bg-white/10">
                  Contact Us
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-border/50 py-8 bg-surface/30">
          <div className="container mx-auto px-6 text-center text-sm text-muted-foreground">
            <p>&copy; 2025 SENTINEL. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}
